import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import apiRequest from '../../../api/request';
import { inr, pct, THEME } from '../utils/trackerConfig';

const AMBER = { main: '#F59E0B', soft: '#FEF3C7', softer: '#FFFBEB', text: '#92400E' };
const KIND_STYLE = {
  Sales: { color: '#065F46', backgroundColor: '#BBF7D0' },
  Fee: { color: '#92400E', backgroundColor: '#FDE68A' },
  Hold: { color: '#5B21B6', backgroundColor: '#DDD6FE' },
};

// No horizontal scrolling in the popup: tables use a fixed layout whose columns add up to 100% of
// the dialog, long text (student, course, subject, names) wraps inside its column, and only short
// things (ids, dates, amounts) stay on one line.
const head = { fontWeight: 700, color: '#fff', backgroundColor: THEME.primary, py: 0.9, px: 1, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' };
const cell = { fontSize: 13, py: 0.7, px: 1, whiteSpace: 'normal', wordBreak: 'break-word' };
const numCell = { ...cell, whiteSpace: 'nowrap', wordBreak: 'normal', fontVariantNumeric: 'tabular-nums' };
const amountCell = { ...numCell, fontWeight: 700 };
// a date may wrap ("10 Apr / 26") rather than get clipped when the dialog is narrow
const dateCell = { ...cell, fontVariantNumeric: 'tabular-nums' };
// On a SMALL screen (`narrow`) the dialog is full screen, cells are tighter and a little smaller, and the less important columns leave
// (Admn No moves under the student's name; the sales list drops # and Subject), so numbers and names are never cut or drawn over each other.
const tableSxFor = (narrow) => ({ tableLayout: 'fixed', width: '100%', ...(narrow ? { '& .MuiTableCell-root': { px: 0.5, fontSize: 11.5 } } : {}) });
const boxSxFor = (narrow) => ({ maxHeight: narrow ? 'calc(100vh - 290px)' : '52vh', overflowX: 'hidden', border: `1px solid ${THEME.primaryLine}`, borderRadius: '10px' });
const fmtDay = (d) => (d ? dayjs(d).format('D MMM YY') : '—');

// column widths in %, each set sums to 100
const Cols = ({ w }) => (
  <colgroup>
    {w.map((x, i) => (
      <col key={i} style={{ width: `${x}%` }} />
    ))}
  </colgroup>
);
const SALES_W = [6, 9, 17, 25, 18, 12, 13];
const SALES_W_SOLDBY = [5, 8, 14, 20, 15, 11, 13, 14];
const PAYMENTS_W = [10, 8, 15, 20, 10, 9, 11, 17];
const TARGETS_W = [32, 14, 10, 16, 28];
const SALES_W_NARROW = [13, 27, 28, 14, 18]; // Admn No, Student, Course, Sale date, Amount
const SALES_W_NARROW_SOLDBY = [12, 22, 22, 13, 15, 16]; // ... plus Sold by
const PAYMENTS_W_NARROW = [12, 24, 20, 12, 14, 18]; // Date, Student (+ Admn No), Course, Counted as, Amount, Collected by
const TARGETS_W_NARROW = [30, 18, 10, 20, 22];

const buildUrl = (request, params) => {
  const q = new URLSearchParams({ metric: request.metric, month: params.month });
  if (params.fromDate && params.toDate) {
    q.set('fromDate', params.fromDate);
    q.set('toDate', params.toDate);
  }
  if (params.skillx) q.set('skillx', 'true');
  if (params.ecpcWing) q.set('ecpcWing', 'true'); // same columns as the table the number came from
  if (params.employeeId) q.set('id', String(params.employeeId));
  const sc = request.scope || {};
  if (sc.projectKey) q.set('projectKey', sc.projectKey);
  if (sc.userIds && sc.userIds.length) q.set('userIds', [...new Set(sc.userIds)].join(','));
  if (sc.ogd) q.set('ogd', 'true');
  if (sc.groupBy) q.set('groupBy', sc.groupBy);
  return `/cro-project-tracker/detail?${q}`;
};

function Stat({ label, value, color, bg }) {
  return (
    <Box sx={{ px: 1.5, py: 0.75, borderRadius: '12px', color, backgroundColor: bg }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', opacity: 0.85 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Box>
  );
}

// ---- No of Sales / Sales: courses grouped by student (admission no) -----------------------------
// No of Sales counts each admission no once, so the list is by student. A student who took more
// than one course is set apart: an amber band with the student total, then each course indented
// under it ("1 of 2", "2 of 2") with an amber edge. A single-course student is one plain row.
function SalesList({ data, narrow }) {
  const tableSx = tableSxFor(narrow);
  const boxSx = boxSxFor(narrow);
  const students = data.students || [];
  const soldByNames = new Set(students.flatMap((s) => (s.soldBy ? [s.soldBy] : s.courses.map((c) => c.soldBy))).filter(Boolean));
  const showSoldBy = soldByNames.size > 1;
  const multi = students.filter((s) => s.courses.length > 1).length;
  const tail = 3 + (showSoldBy ? 1 : 0); // Course, Subject, Sale date (+ Sold by)

  let n = 0;
  const rows = students.flatMap((st, si) => {
    n += 1;
    const num = n;
    const soldBy = (c) => c.soldBy || st.soldBy;
    if (st.courses.length === 1) {
      const c = st.courses[0];
      return [
        <TableRow key={`s${si}`} hover>
          {!narrow && <TableCell align="center" sx={{ ...numCell, color: '#94A3B8', fontWeight: 600 }}>{num}</TableCell>}
          <TableCell align="center" sx={{ ...numCell, fontWeight: 600 }}>{st.admnNo}</TableCell>
          <TableCell sx={{ ...cell, fontWeight: 600 }}>{st.studentName || '—'}</TableCell>
          <TableCell sx={cell}>{c.courseName || '—'}</TableCell>
          {!narrow && <TableCell sx={{ ...cell, color: c.subjectNames ? 'inherit' : '#94A3B8' }}>{c.subjectNames || '—'}</TableCell>}
          <TableCell align="center" sx={dateCell}>{fmtDay(c.saleDate)}</TableCell>
          {showSoldBy && <TableCell sx={cell}>{soldBy(c) || '—'}</TableCell>}
          <TableCell align="center" sx={{ ...amountCell, color: '#4338CA' }}>{inr(c.saleValue)}</TableCell>
        </TableRow>,
      ];
    }
    const band = (
      <TableRow key={`b${si}`} sx={{ backgroundColor: AMBER.soft }}>
        {!narrow && <TableCell align="center" sx={{ ...numCell, fontWeight: 800, color: AMBER.text, borderLeft: `4px solid ${AMBER.main}` }}>{num}</TableCell>}
        <TableCell align="center" sx={{ ...numCell, fontWeight: 800, color: AMBER.text, ...(narrow ? { borderLeft: `4px solid ${AMBER.main}` } : {}) }}>{st.admnNo}</TableCell>
        <TableCell sx={{ ...cell, fontWeight: 800, color: AMBER.text }}>{st.studentName || '—'}</TableCell>
        <TableCell colSpan={tail - (narrow ? 1 : 0)} sx={cell}>
          <Chip
            label={`${st.courses.length} courses, one admission no`}
            size="small"
            sx={{ height: 20, fontWeight: 700, color: '#fff', backgroundColor: AMBER.main }}
          />
          <Typography component="span" variant="caption" sx={{ ml: 1, color: AMBER.text }}>
            counted once in No of Sales · student total →
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ ...amountCell, color: AMBER.text }}>{inr(st.total)}</TableCell>
      </TableRow>
    );
    const courseRows = st.courses.map((c, ci) => (
      <TableRow key={`c${si}-${ci}`} hover sx={{ backgroundColor: AMBER.softer }}>
        <TableCell colSpan={narrow ? 2 : 3} sx={{ ...cell, pl: narrow ? 1.5 : 3, color: AMBER.text, fontWeight: 700, borderLeft: `4px solid ${AMBER.main}` }}>
          ↳ Course {ci + 1} of {st.courses.length}
        </TableCell>
        <TableCell sx={cell}>{c.courseName || '—'}</TableCell>
        {!narrow && <TableCell sx={{ ...cell, color: c.subjectNames ? 'inherit' : '#94A3B8' }}>{c.subjectNames || '—'}</TableCell>}
        <TableCell align="center" sx={dateCell}>{fmtDay(c.saleDate)}</TableCell>
        {showSoldBy && <TableCell sx={cell}>{soldBy(c) || '—'}</TableCell>}
        <TableCell align="center" sx={{ ...amountCell, color: '#4338CA' }}>{inr(c.saleValue)}</TableCell>
      </TableRow>
    ));
    return [band, ...courseRows];
  });

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
        <Stat label="No of Sales (students)" value={inr(data.totals.students)} color={THEME.primaryDark} bg={THEME.primaryLine} />
        <Stat
          label="Courses"
          value={inr(data.totals.courses)}
          color={data.totals.courses > data.totals.students ? AMBER.text : THEME.primaryDark}
          bg={data.totals.courses > data.totals.students ? AMBER.soft : THEME.primaryLine}
        />
        <Stat label="Sales (sale amount)" value={`₹ ${inr(data.totals.saleValue)}`} color="#065F46" bg="#D1FAE5" />
      </Box>
      <Alert severity="info" icon={false} sx={{ mb: 1.25, py: 0, backgroundColor: THEME.primarySoft, color: THEME.primaryDark }}>
        No of Sales counts each admission no once.
        {multi > 0
          ? ` ${multi} student${multi === 1 ? '' : 's'} took more than one course - set apart in amber, with each course listed under the student.`
          : ' Every student here took one course.'}
      </Alert>
      <TableContainer sx={boxSx}>
        <Table size="small" stickyHeader sx={tableSx}>
          <Cols w={narrow ? (showSoldBy ? SALES_W_NARROW_SOLDBY : SALES_W_NARROW) : showSoldBy ? SALES_W_SOLDBY : SALES_W} />
          <TableHead>
            <TableRow>
              {!narrow && <TableCell align="center" sx={head}>#</TableCell>}
              <TableCell align="center" sx={head}>Admn No</TableCell>
              <TableCell sx={head}>Student</TableCell>
              <TableCell sx={head}>Course</TableCell>
              {!narrow && <TableCell sx={head}>Subject</TableCell>}
              <TableCell align="center" sx={head}>Sale date</TableCell>
              {showSoldBy && <TableCell sx={head}>Sold by</TableCell>}
              <TableCell align="center" sx={head}>Sale amount (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? rows : (
              <TableRow>
                <TableCell colSpan={(narrow ? 5 : 7) + (showSoldBy ? 1 : 0)} align="center" sx={{ py: 4, color: '#64748B' }}>Nothing to show.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// ---- Sales Revenue / Fee Revenue / Total Revenue: the payments -----------------------------------
function PaymentsList({ data, metric, narrow }) {
  const tableSx = tableSxFor(narrow);
  const boxSx = boxSxFor(narrow);
  const rows = data.rows || [];
  const byKind = rows.reduce((t, r) => ({ ...t, [r.kind]: (t[r.kind] || 0) + r.amount }), {});
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
        <Stat label="Payments" value={inr(data.totals.count)} color={THEME.primaryDark} bg={THEME.primaryLine} />
        <Stat label="Amount collected" value={`₹ ${inr(data.totals.amount)}`} color="#065F46" bg="#D1FAE5" />
        {metric === 'totalRevenue' && (
          <>
            <Stat label="Sales Revenue" value={`₹ ${inr((byKind.Sales || 0) + (byKind.Hold || 0))}`} color="#047857" bg="#ECFDF5" />
            <Stat label="Fee Revenue" value={`₹ ${inr(byKind.Fee || 0)}`} color="#B45309" bg="#FFFBEB" />
          </>
        )}
      </Box>
      <TableContainer sx={boxSx}>
        <Table size="small" stickyHeader sx={tableSx}>
          <Cols w={narrow ? PAYMENTS_W_NARROW : PAYMENTS_W} />
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={head}>Date</TableCell>
              {!narrow && <TableCell align="center" sx={head}>Admn No</TableCell>}
              <TableCell sx={head}>Student</TableCell>
              <TableCell sx={head}>Course</TableCell>
              {!narrow && <TableCell sx={head}>Fee type</TableCell>}
              <TableCell align="center" sx={head}>Counted as</TableCell>
              <TableCell align="center" sx={head}>Amount (₹)</TableCell>
              <TableCell sx={head}>Collected by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={narrow ? 6 : 8} align="center" sx={{ py: 4, color: '#64748B' }}>Nothing to show.</TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.incomeId} hover>
                <TableCell align="center" sx={dateCell}>{fmtDay(r.date)}</TableCell>
                {!narrow && <TableCell align="center" sx={{ ...numCell, fontWeight: 600 }}>{r.admnNo || '—'}</TableCell>}
                <TableCell sx={{ ...cell, fontWeight: 600 }}>
                  {r.studentName || '—'}
                  {narrow && r.admnNo && (
                    <Box component="div" sx={{ fontSize: 10.5, fontWeight: 500, color: '#64748B' }}>
                      #{r.admnNo} · {r.feeType}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={cell}>{r.courseName || '—'}</TableCell>
                {!narrow && <TableCell sx={cell}>{r.feeType}</TableCell>}
                <TableCell align="center" sx={numCell}>
                  <Chip label={r.kind} size="small" sx={{ height: narrow ? 18 : 20, fontWeight: 700, ...(narrow ? { fontSize: 10.5, '& .MuiChip-label': { px: 0.6 } } : {}), ...KIND_STYLE[r.kind] }} />
                </TableCell>
                <TableCell align="center" sx={{ ...amountCell, color: '#1E3A8A' }}>{inr(r.amount)}</TableCell>
                <TableCell sx={cell}>
                  {r.collectorName || '—'}
                  {r.viaEnroller && (
                    <Box
                      component="span"
                      title="Collected by someone outside the tracked teams (e.g. Finance) - credited to the person who made the sale"
                      sx={{ display: 'inline-block', mt: 0.25, px: 0.9, py: 0.15, borderRadius: '10px', fontSize: 11, fontWeight: 600, lineHeight: 1.3, color: '#9A3412', backgroundColor: '#FFEDD5', whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      credited to {r.creditedTo}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// ---- Total Target: the target rows ---------------------------------------------------------------
function TargetsList({ data, narrow }) {
  const tableSx = tableSxFor(narrow);
  const boxSx = boxSxFor(narrow);
  const rows = data.rows || [];
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
        <Stat label="Target rows" value={inr(data.totals.count)} color={THEME.primaryDark} bg={THEME.primaryLine} />
        <Stat label="Total target" value={`₹ ${inr(data.totals.target)}`} color="#5B21B6" bg="#EDE9FE" />
      </Box>
      <TableContainer sx={boxSx}>
        <Table size="small" stickyHeader sx={tableSx}>
          <Cols w={narrow ? TARGETS_W_NARROW : TARGETS_W} />
          <TableHead>
            <TableRow>
              <TableCell sx={head}>CRO</TableCell>
              <TableCell align="center" sx={head}>Target (₹)</TableCell>
              <TableCell align="center" sx={head}>Slot</TableCell>
              <TableCell align="center" sx={head}>Set on</TableCell>
              <TableCell sx={head}>Set by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748B' }}>No target has been set for this month.</TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.targetId} hover>
                <TableCell sx={{ ...cell, fontWeight: 600 }}>{r.assignedName}</TableCell>
                <TableCell align="center" sx={{ ...amountCell, color: '#6D28D9' }}>{inr(r.target)}</TableCell>
                <TableCell align="center" sx={numCell}>{inr(r.slot)}</TableCell>
                <TableCell align="center" sx={dateCell}>{fmtDay(r.setOn)}</TableCell>
                <TableCell sx={cell}>{r.setBy || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// ---- TAR / ARPU: a formula, no list -----------------------------------------------------------------
function Formula({ formula }) {
  const isTar = formula.kind === 'tar';
  const left = isTar ? formula.revenue : formula.revenue;
  const right = isTar ? formula.target : formula.admissions;
  const result = isTar ? pct(formula.tar) : `₹ ${inr(right > 0 ? left / right : 0)}`;
  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="overline" sx={{ color: THEME.primaryDark, fontWeight: 700 }}>
        {isTar ? 'TAR = Total Revenue ÷ Total Target' : 'ARPU = OGD Revenue ÷ OGD Admissions'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
        <Stat label={isTar ? 'Total Revenue' : 'OGD Revenue'} value={`₹ ${inr(left)}`} color="#1E3A8A" bg="#DBEAFE" />
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#64748B' }}>÷</Typography>
        <Stat label={isTar ? 'Total Target' : 'OGD Admissions'} value={isTar ? `₹ ${inr(right)}` : inr(right)} color="#5B21B6" bg="#EDE9FE" />
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#64748B' }}>=</Typography>
        <Stat label={isTar ? 'TAR' : 'ARPU'} value={result} color="#065F46" bg="#BBF7D0" />
      </Box>
    </Box>
  );
}

const toTsv = (data) => {
  if (data.type === 'sales') {
    const rows = [['Admn No', 'Student', 'Course', 'Subject', 'Sale date', 'Sold by', 'Sale amount']];
    data.students.forEach((s) =>
      s.courses.forEach((c) => rows.push([s.admnNo, s.studentName || '', c.courseName || '', c.subjectNames || '', c.saleDate || '', c.soldBy || s.soldBy || '', Math.round(c.saleValue)])),
    );
    return rows;
  }
  if (data.type === 'payments') {
    return [
      ['Date', 'Admn No', 'Student', 'Course', 'Fee type', 'Counted as', 'Amount', 'Collected by', 'Credited to'],
      ...data.rows.map((r) => [r.date, r.admnNo || '', r.studentName || '', r.courseName || '', r.feeType, r.kind, Math.round(r.amount), r.collectorName || '', r.creditedTo]),
    ];
  }
  return [['CRO', 'Target', 'Slot', 'Set on', 'Set by'], ...data.rows.map((r) => [r.assignedName, r.target, r.slot, r.setOn, r.setBy || ''])];
};

/**
 * The rows behind a clicked number. `request` = { metric, title, scope, formula } from the page;
 * `params` = the page's window/filters (month, fromDate/toDate, skillx, employeeId), so the list is
 * always for the same period and scope as the table the number came from.
 */
export default function DetailDialog({ request, params, onClose }) {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const [toast, setToast] = useState('');
  const isFormula = Boolean(request && request.formula);

  useEffect(() => {
    if (!request) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    if (isFormula) return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    apiRequest({ method: 'get', url: buildUrl(request, params) })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'error') setState({ loading: false, error: res.message || 'Request failed', data: null });
        else setState({ loading: false, error: null, data: res?.data || null });
      })
      .catch((e) => {
        if (!cancelled) setState({ loading: false, error: e?.message || 'Could not load the rows', data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [request, params, isFormula]);

  const windowText = useMemo(() => {
    const w = state.data?.window || (params.fromDate ? { fromDate: params.fromDate, toDate: params.toDate } : null);
    if (!w) return dayjs(params.month).format('MMMM YYYY');
    return `${dayjs(w.fromDate).format('D MMM')} – ${dayjs(w.toDate).format('D MMM YYYY')}`;
  }, [state.data, params]);

  const handleCopy = async () => {
    const tsv = toTsv(state.data).map((r) => r.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      setToast('Copied - paste into Sheets or Excel');
    } catch (e) {
      setToast('Copy was blocked by the browser');
    }
  };

  // Excel export of exactly the rows in the list (one row per course / payment / target, so it
  // pivots cleanly); the file is named after the clicked number and its period
  const handleExport = () => {
    const ws = XLSX.utils.aoa_to_sheet(toTsv(state.data));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Details');
    const base = `${request?.title || 'details'} ${windowText}`.replace(/[^\w]+/g, '_').replace(/^_+|_+$/g, '');
    XLSX.writeFile(wb, `${base}.xlsx`);
  };

  const { loading, error, data } = state;
  // a small screen gets the dialog full screen, with tighter tables (see tableSxFor)
  const narrow = useMediaQuery('(max-width:700px)');

  return (
    <Dialog open={Boolean(request)} onClose={onClose} fullWidth fullScreen={narrow && !isFormula} maxWidth={isFormula ? 'sm' : 'xl'} PaperProps={{ sx: { borderRadius: narrow && !isFormula ? 0 : '16px', overflow: 'hidden' } }}>
      <Box sx={{ backgroundColor: THEME.primary, color: '#fff', px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }} noWrap>
            {request?.title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {windowText}
          </Typography>
        </Box>
        {data && !isFormula && (
          <>
            <Button size="small" onClick={handleCopy} startIcon={<ContentCopyRoundedIcon />} sx={{ color: '#fff', fontWeight: 700, border: '1px solid rgba(255,255,255,0.5)' }}>
              Copy
            </Button>
            <Button
              size="small"
              onClick={handleExport}
              startIcon={<FileDownloadRoundedIcon />}
              sx={{ color: THEME.primaryDark, fontWeight: 700, backgroundColor: '#fff', '&:hover': { backgroundColor: THEME.primaryLine } }}
            >
              Export
            </Button>
          </>
        )}
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close">
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 2, px: narrow ? 1 : 3, backgroundColor: '#FAFAFF', overflowX: 'hidden' }}>
        {request && isFormula && <Formula formula={request.formula} />}
        {request && !isFormula && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {request && !isFormula && error && <Alert severity="error">Could not load the rows: {error}</Alert>}
        {request && !isFormula && data && data.type === 'sales' && <SalesList data={data} narrow={narrow} />}
        {request && !isFormula && data && data.type === 'payments' && <PaymentsList data={data} metric={data.metric} narrow={narrow} />}
        {request && !isFormula && data && data.type === 'targets' && <TargetsList data={data} narrow={narrow} />}
      </DialogContent>
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Dialog>
  );
}
